// offerableItems.js - Build the flat list of "leaf" items a salon offer can
// target, and the identity key used to store per-item offer config.
//
// Must produce byte-identical keys to the mobile app's equivalent
// (UlloorStyles/src/utils/salonServiceUtils.js: buildOfferItemKey /
// getAllBookableLeafItems), since both apps read/write the same salon doc's
// profile.servicesData.serviceOffer. Name/gender based (not `.id`-based)
// because published services carry no id fields.

const norm = (v) => (v || '').toString().trim().toLowerCase();

export function buildOfferItemKey({ categoryName, serviceName, genderType, subServiceName, comboTitle, isCombo }) {
  if (isCombo) {
    return `combo|${norm(comboTitle)}||`;
  }
  return `${norm(categoryName)}|${norm(serviceName)}|${norm(genderType)}|${norm(subServiceName)}`;
}

// Flat list of every bookable "leaf" item an offer can target: services with
// no subServices, each individual subService (never the parent — the
// subservice is the actual bookable unit whenever variants exist), and
// combos.
export function getAllBookableLeafItems(servicesData) {
  const items = [];

  (servicesData?.serviceCategories || []).forEach((cat) => {
    const categoryName = cat.name || cat.categoryName || 'Services';
    (cat.services || cat.serviceList || []).forEach((svc) => {
      const genderType = norm(svc.genderType ?? svc.gender ?? cat.genderType);
      const serviceName = svc.name || 'Service';
      if (Array.isArray(svc.subServices) && svc.subServices.length > 0) {
        svc.subServices.forEach((sub) => {
          const subServiceName = sub.name || 'Variant';
          items.push({
            itemKey: buildOfferItemKey({ categoryName, serviceName, genderType, subServiceName }),
            categoryName,
            serviceName,
            subServiceName,
            genderType,
            comboTitle: null,
            isCombo: false,
            displayName: `${serviceName} - ${subServiceName}`,
            price: sub.price
          });
        });
      } else {
        items.push({
          itemKey: buildOfferItemKey({ categoryName, serviceName, genderType }),
          categoryName,
          serviceName,
          subServiceName: null,
          genderType,
          comboTitle: null,
          isCombo: false,
          displayName: serviceName,
          price: svc.price
        });
      }
    });
  });

  (servicesData?.combos || []).forEach((combo) => {
    const comboTitle = combo.title || combo.name || 'Combo';
    items.push({
      itemKey: buildOfferItemKey({ comboTitle, isCombo: true }),
      categoryName: null,
      serviceName: null,
      subServiceName: null,
      genderType: null,
      comboTitle,
      isCombo: true,
      displayName: comboTitle,
      price: combo.totalPrice ?? combo.price ?? 0
    });
  });

  return items;
}
